import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Send, Check, X, ChevronRight, Loader2, Target, Lightbulb, MessageSquare, AlertTriangle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

export default function FragetechnikPage() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [questionDetails, setQuestionDetails] = useState([]);
  const [focusAreas, setFocusAreas] = useState([]);
  const [personalized, setPersonalized] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answer, setAnswer] = useState("");
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.post("/learning/fragetechnik/start").then(res => {
      setQuestions(res.data.questions || []);
      setQuestionDetails(res.data.question_details || []);
      setFocusAreas(res.data.focus_areas || []);
      setPersonalized(res.data.personalized || false);
      setLoading(false);
    }).catch(() => { toast.error("Fehler beim Laden"); setLoading(false); });
  }, []);

  const submitAnswer = async () => {
    if (!answer.trim() || checking) return;
    setChecking(true);
    setResult(null);
    try {
      const detail = questionDetails[currentIdx] || {};
      const res = await api.post("/learning/fragetechnik/check", {
        question: questions[currentIdx],
        answer: answer.trim(),
        weakness_context: detail.weakness_label || "Allgemein"
      });
      setResult(res.data);
      setHistory(prev => [...prev, {
        question: questions[currentIdx],
        answer: answer.trim(),
        weakness: detail.weakness_label || "Allgemein",
        ...res.data
      }]);
    } catch { toast.error("Analyse fehlgeschlagen"); }
    setChecking(false);
  };

  const nextQuestion = () => {
    if (currentIdx + 1 < questions.length) {
      setCurrentIdx(currentIdx + 1);
      setAnswer("");
      setResult(null);
    }
  };

  const score = history.filter(h => h.answered && h.concrete).length;
  const weaknessShown = history.filter(h => h.weakness_shown).length;

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  // Completion screen
  if (currentIdx >= questions.length || (history.length === questions.length && result)) {
    const pct = Math.round((score / questions.length) * 100);
    return (
      <div className="page-transition max-w-2xl mx-auto space-y-6" data-testid="fragetechnik-complete">
        <Button variant="ghost" size="icon" onClick={() => navigate("/lernpfad")} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="text-center space-y-4 py-6">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
            <Target className="w-10 h-10 text-primary" />
          </div>
          <h1 className="font-heading text-3xl font-bold">Fragetechnik-Ergebnis</h1>
          <p className="font-body text-lg text-muted-foreground">{score} von {questions.length} Fragen korrekt beantwortet</p>
          <Progress value={pct} className="h-3 max-w-xs mx-auto" />
          {weaknessShown > 0 && (
            <p className="font-body text-sm text-warning">Bei {weaknessShown} Antworten zeigte sich Ihre bekannte Schwäche</p>
          )}
          <p className="font-body text-sm text-muted-foreground">
            {pct >= 80 ? "Hervorragend! Sie beantworten Fragen präzise und konkret." :
             pct >= 50 ? "Guter Ansatz! Arbeiten Sie weiter an konkreteren Antworten." :
             "Hier gibt es noch Potenzial. Üben Sie, Fragen direkt und konkret zu beantworten."}
          </p>
        </div>
        <div className="space-y-3">
          {history.map((h, i) => (
            <Card key={i} className={`border-l-4 ${h.answered && h.concrete ? "border-l-success" : h.weakness_shown ? "border-l-destructive" : "border-l-warning"}`}>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-body text-xs font-medium text-muted-foreground">Frage {i + 1}</p>
                  {h.weakness !== "Allgemein" && <Badge variant="secondary" className="text-[10px] font-body py-0">{h.weakness}</Badge>}
                </div>
                <p className="font-body text-sm font-medium mb-2">{h.question}</p>
                <p className="font-body text-xs text-muted-foreground">{h.feedback}</p>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="flex gap-3">
          <Button className="flex-1 font-body" onClick={() => { setCurrentIdx(0); setHistory([]); setResult(null); setAnswer(""); }} data-testid="retry-btn">Nochmal</Button>
          <Button variant="outline" className="flex-1 font-body" onClick={() => navigate("/lernpfad")} data-testid="back-to-path">Zum Lernpfad</Button>
        </div>
      </div>
    );
  }

  const currentDetail = questionDetails[currentIdx] || {};

  return (
    <div className="page-transition max-w-2xl mx-auto space-y-6" data-testid="fragetechnik-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/lernpfad")} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="font-heading text-xl font-bold">Fragetechnik</h1>
          <p className="font-body text-xs text-muted-foreground">
            {personalized ? "Personalisiert auf Ihre Schwächen" : "Lernen Sie, Fragen präzise zu beantworten"}
          </p>
        </div>
        <Badge variant="secondary" className="font-body">{currentIdx + 1} / {questions.length}</Badge>
      </div>

      <Progress value={((currentIdx + (result ? 1 : 0)) / questions.length) * 100} className="h-1.5" />

      {/* Focus areas banner (personalized) */}
      {personalized && currentIdx === 0 && !result && focusAreas.length > 0 && (
        <Card className="border-warning/30 bg-warning/5">
          <CardContent className="pt-4 pb-3">
            <p className="font-body text-xs font-medium text-warning mb-2">Ihre Schwerpunkte heute:</p>
            <div className="flex flex-wrap gap-2">
              {focusAreas.map(f => (
                <Badge key={f.key} variant="outline" className="font-body text-xs gap-1 border-warning/30">
                  <AlertTriangle className="w-3 h-3 text-warning" />
                  {f.label} ({f.score}/10)
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Question */}
      <AnimatePresence mode="wait">
        <motion.div key={currentIdx} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
          <Card className="border-primary/20 bg-primary/5" data-testid="question-card">
            <CardContent className="pt-6 pb-5">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-body text-xs text-primary font-medium">Der Gutachter fragt:</p>
                    {currentDetail.weakness !== "allgemein" && (
                      <Badge variant="secondary" className="text-[10px] font-body py-0">{currentDetail.weakness_label}</Badge>
                    )}
                  </div>
                  <p className="font-body text-base font-medium leading-relaxed">{questions[currentIdx]}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </AnimatePresence>

      {/* Answer input */}
      {!result && (
        <div className="space-y-3">
          <Input
            value={answer}
            onChange={e => setAnswer(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitAnswer(); } }}
            placeholder="Ihre Antwort..."
            className="h-12 font-body"
            disabled={checking}
            data-testid="answer-input"
          />
          <Button
            className="w-full h-11 font-body bg-primary text-primary-foreground gap-2"
            onClick={submitAnswer}
            disabled={!answer.trim() || checking}
            data-testid="submit-answer"
          >
            {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {checking ? "Wird geprüft..." : "Antwort prüfen"}
          </Button>
        </div>
      )}

      {/* Result */}
      {result && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Badge variant={result.answered ? "default" : "destructive"} className="font-body gap-1 py-1">
              {result.answered ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
              {result.answered ? "Frage beantwortet" : "Frage nicht beantwortet"}
            </Badge>
            <Badge variant={result.concrete ? "default" : "secondary"} className="font-body gap-1 py-1">
              {result.concrete ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
              {result.concrete ? "Konkret" : "Zu allgemein"}
            </Badge>
            {result.weakness_shown && (
              <Badge variant="destructive" className="font-body gap-1 py-1">
                <AlertTriangle className="w-3 h-3" /> Schwäche sichtbar
              </Badge>
            )}
          </div>

          <Card className="border-border/50">
            <CardContent className="pt-6 pb-4 space-y-3">
              <p className="font-body text-sm leading-relaxed">{result.feedback}</p>
              {result.tip && (
                <div className="flex items-start gap-2 bg-warning/5 p-3 rounded-lg">
                  <Lightbulb className="w-4 h-4 text-warning mt-0.5 flex-shrink-0" />
                  <p className="font-body text-sm text-muted-foreground">{result.tip}</p>
                </div>
              )}
              {result.example && (
                <div className="bg-success/5 p-3 rounded-lg">
                  <p className="font-body text-xs font-medium text-success mb-1">Beispiel-Antwort:</p>
                  <p className="font-body text-sm text-muted-foreground italic">{result.example}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Button className="w-full h-11 font-body gap-2" onClick={nextQuestion} data-testid="next-question">
            {currentIdx + 1 < questions.length ? (
              <>Nächste Frage <ChevronRight className="w-4 h-4" /></>
            ) : (
              <>Ergebnis anzeigen <Target className="w-4 h-4" /></>
            )}
          </Button>
        </motion.div>
      )}
    </div>
  );
}
